import type { ContactJson } from 'app/types/entities'
import type { ScheduleEligibility, SchedulingLocation } from 'app/types/scheduling'
import { pmsGate } from 'lib/pmsCapabilities'

export const REASON_NO_PMS = `Connect your practice management system to book appointments here`
export const REASON_NO_PATIENT_LINK = `Link this contact to a patient record to schedule`
export const REASON_LOCATION_NOT_SET_UP = `Online scheduling isn’t set up for this location`
export const REASON_NO_LOCATION = `This contact isn’t assigned to a location`

/**
 * In-app booking runs the Kolla online-scheduler flow, so it needs a patient-linked contact at a
 * Kolla location with the Scheduler or AI Agent product live. The contact drawer and the inbox
 * share this rule.
 */
export function getScheduleEligibility(
  contact: ContactJson,
  location: SchedulingLocation | undefined,
): ScheduleEligibility {
  if (!location) return { state: `pending` }

  const gate = pmsGate([location], `inAppBooking`)
  if (gate === `padlock`) return { state: `locked`, reason: REASON_NO_PMS }
  if (gate === `hidden`) return { state: `hidden` }

  if (!contact.patientIds?.length) return { state: `blocked`, reason: REASON_NO_PATIENT_LINK }
  if (!contact.locationId) return { state: `blocked`, reason: REASON_NO_LOCATION }

  // The inbox reads one Location, its scoped one, and the wizard later runs against that same
  // record, so a contact from another location must not borrow this location's flags.
  const locationReady = contact.locationId === location.id && (!!location.isSchedulerLive || !!location.isAIAgentLive)
  if (!locationReady) return { state: `blocked`, reason: REASON_LOCATION_NOT_SET_UP }

  return { state: `ready` }
}
